/**
 * Modale "Voir toutes les actions recommandées".
 *
 * Affiche toutes les actions recommandées générées par le backend en plein écran,
 * avec filtres priorité + recherche. Permet de cliquer une action pour ouvrir
 * la modale de détail (`open('recommended', client, action)`).
 */
import React, { useMemo, useState } from 'react';
import { Dialog, DialogContent } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import {
  X, Search, ArrowRight, Activity, TrendingUp,
} from 'lucide-react';
import { useClientActions } from './ClientActionsContext';
import { PRIORITY_BADGE, PRIORITY_LABEL } from './shared';

export default function AllRecommendedActionsModal({ open, onOpenChange, actions = [], clients = [] }) {
  const { open: openAction } = useClientActions();
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');

  const targetClient = useMemo(() => {
    return (
      clients.find((cl) => cl.risk === 'a_relancer') ||
      clients.find((cl) => cl.risk === 'a_surveiller') ||
      clients[0]
    );
  }, [clients]);

  const filtered = useMemo(() => {
    let rows = actions;
    if (filter !== 'all') rows = rows.filter((a) => a.priority === filter);
    if (q.trim()) {
      const qq = q.toLowerCase();
      rows = rows.filter((a) =>
        (a.title || '').toLowerCase().includes(qq) ||
        (a.description || '').toLowerCase().includes(qq)
      );
    }
    return rows;
  }, [actions, filter, q]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col" data-testid="all-recommended-modal">
        <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-4 border-b border-gray-100">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Toutes les actions recommandées</h2>
              <p className="text-xs text-gray-500">
                {filtered.length} action{filtered.length > 1 ? 's' : ''} prioritaire{filtered.length > 1 ? 's' : ''} à traiter
              </p>
            </div>
          </div>
          <button onClick={() => onOpenChange(false)} className="text-gray-400 hover:text-gray-600" data-testid="close-all-rec">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Filtres */}
        <div className="px-6 py-3 border-b border-gray-100 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher une action..."
              className="pl-9"
              data-testid="all-rec-search"
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44" data-testid="all-rec-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes priorités</SelectItem>
              <SelectItem value="haute">Haute priorité</SelectItem>
              <SelectItem value="moyenne">Moyenne</SelectItem>
              <SelectItem value="basse">Faible</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Liste */}
        <div className="overflow-auto flex-1 px-6 py-4 space-y-2">
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Activity className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Aucune action recommandée pour ce filtre.</p>
            </div>
          ) : (
            filtered.map((a) => (
              <button
                key={a.id || a.title}
                onClick={() => targetClient && openAction('recommended', targetClient, a)}
                className="w-full text-left border border-gray-100 rounded-lg p-3 hover:bg-gray-50 transition-colors flex items-start gap-3"
                data-testid={`all-rec-row-${a.id || ''}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="text-sm font-medium text-gray-900">{a.title}</h4>
                    <Badge className={`${PRIORITY_BADGE[a.priority] || ''} border text-[10px] capitalize shrink-0`}>
                      {PRIORITY_LABEL[a.priority] || a.priority}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{a.description}</p>
                  {a.cta && (
                    <span className="text-[11px] text-emerald-600 mt-1 inline-flex items-center gap-1">
                      {a.cta} <ArrowRight className="w-3 h-3" />
                    </span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-end">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} data-testid="all-rec-close-btn">
            Fermer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
